import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { Stack, SvgIcon } from "@mui/material";
import { FaroTextButton } from "@components/common/faro-text-button";
import { withEllipsisTwoLines } from "@styles/common-styles";
import { useUserContext } from "@pages/account-settings/user-provider";
import { FaroText } from "@faro-lotv/flat-ui";
import { DropdownButton } from "@components/dropdown/dropdown-button";
import { sphereColors } from "@styles/common-colors";

interface Props {
  /** Unique identifier of the integration, used to retrieve integration data and perform specific actions. */
  integrationId: SphereDashboardAPITypes.IntegrationId;

  /**
   * Set containing the IDs of integrations currently enabled.
   * Used to check if the current integration is enabled.
   */
  enabledIntegrationIds: Set<SphereDashboardAPITypes.IntegrationId>;

  /** Called when integration should be enabled */
  onEnable(integrationId: SphereDashboardAPITypes.IntegrationId): void;

  /** Called when integration should be disabled */
  onDisable(integrationId: SphereDashboardAPITypes.IntegrationId): void;
}

/** Component that renders a single row representing an integration. */
export function IntegrationRow({
  integrationId,
  enabledIntegrationIds,
  onEnable,
  onDisable,
}: Props): JSX.Element {
  const integration = INTEGRATION_INFO[integrationId];
  const isEnabled = enabledIntegrationIds.has(integrationId);
  const { user } = useUserContext();

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      paddingX="1.25rem"
      justifyContent="space-between"
      width="100%"
      gap="1rem"
      marginY="1.75rem"
    >
      <Stack flexDirection="row" gap="1.875rem" alignItems="center">
        <SvgIcon
          inheritViewBox={true}
          component={integration.icon}
          sx={{ width: "2.5rem", height: "2.5rem" }}
        />
        <Stack>
          <FaroText variant="heading20">{integration.displayName}</FaroText>
          <FaroText
            variant="bodyS"
            marginTop="0.25rem"
            title={integration.description}
            sx={{ ...withEllipsisTwoLines }}
          >
            {isEnabled ? user.mailAddress : integration.description}
          </FaroText>
        </Stack>
      </Stack>

      {isEnabled ? (
        <DropdownButton
          label="Connected"
          statusChipColor={sphereColors.green600}
          actions={[
            {
              label: "Disconnect...",
              onClick: () => onDisable(integrationId),
            },
          ]}
        />
      ) : (
        <FaroTextButton onClick={() => onEnable(integrationId)}>
          Connect
        </FaroTextButton>
      )}
    </Stack>
  );
}
